// RecruiterDashboard.jsx (Modified to use the new components)
import React, { useState, useEffect, useCallback } from "react";
import {
  Button,
  Box,
  CircularProgress,
  Snackbar,
  Alert,
  Pagination,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { format } from "date-fns";

import axios from "axios";
import useUserId from "hooks/useUserId";
import useCarnivalWebSocket from "hooks/useCarnivalWebSocket";
import CarnivalVideoCall from "./CarnivalVideoCall";
import { useNavigate } from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CarnivalCard from "./CarnivalCard";
import CarnivalFormDialog from "./CarnivalFormDialog";
import ShareCarnivalDialog from "./ShareCarnivalDialog";
import SearchBarCarnivals from "../SearchBarCarnivals";
import { useTranslation } from "react-i18next";
// Create a modern theme with midnight blue colors
const theme = createTheme({
  palette: {
    primary: {
      main: "#0017E7", // Electric blue
      light: "#4d5aff",
      dark: "#0010a3",
      contrastText: "#fff",
    },
    secondary: {
      main: "#1e40af", // Complementary blue
      light: "#3b82f6",
      dark: "#1e3a8a",
      contrastText: "#fff",
    },
    background: {
      default: "#f8fafc", // Very light blue-gray
      paper: "#ffffff",
    },
    text: {
      primary: "#1e293b", // Dark slate
      secondary: "#475569", // Medium slate
    },
    divider: "#e2e8f0", // Light slate
    info: {
      main: "#0017E7", // Electric blue for info
    },
    success: {
      main: "#059669", // Emerald green
    },
    warning: {
      main: "#d97706", // Amber
    },
    error: {
      main: "#dc2626", // Red
    },
  },
  shape: {
    borderRadius: 10, // Rounded corners
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          boxShadow: "none",
          fontWeight: 500,
          borderRadius: 8,
        },
        containedPrimary: {
          background: "linear-gradient(45deg, #0017E7 30%, #1e40af 90%)",
          "&:hover": {
            background: "linear-gradient(45deg, #0010a3 30%, #1e3a8a 90%)",
            boxShadow: "0px 4px 15px rgba(0, 23, 231, 0.4)",
          },
        },
        outlinedPrimary: {
          borderColor: "#0017E7",
          color: "#0017E7",
          "&:hover": {
            borderColor: "#0010a3",
            backgroundColor: "rgba(0, 23, 231, 0.04)",
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: "0px 3px 15px rgba(0, 0, 0, 0.05)",
          transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
          "&:hover": {
            transform: "translateY(-3px)",
            boxShadow: "0px 8px 25px rgba(0, 0, 0, 0.1)",
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
        },
      },
    },
  },
});

const RecruiterDashboard = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  // State management
  const [openDialog, setOpenDialog] = useState(false);
  const [carnivals, setCarnivals] = useState([]);
  const [selectedCarnival, setSelectedCarnival] = useState(null);
  const [viewMode, setViewMode] = useState("active");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    totalItems: 0,
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedCarnivalToEdit, setSelectedCarnivalToEdit] = useState(null);
  const [showingResults, setShowingResults] = useState(false);
  const [selectedCarnivalForResults, setSelectedCarnivalForResults] =
    useState(null);

  const apiUrl = process.env.REACT_APP_API_URL;
  const { userId } = useUserId();

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    deadline: "",
    start_time: "",
    max_participants: 100,
  });

  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [sharedCarnival, setSharedCarnival] = useState(null);
  const [shareLink, setShareLink] = useState("");
  const [copySuccess, setCopySuccess] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [confirmUnarchiveDialogOpen, setConfirmUnarchiveDialogOpen] =
    useState(false);
  const [carnivalToUnarchive, setCarnivalToUnarchive] = useState(null);

  // WebSocket state for real-time updates
  const [carnivalUpdates, setCarnivalUpdates] = useState({});

  const handleSearchResults = (results) => {
    setSearchResults(results);
    setLoading(false);
  };

  // WebSocket handlers for real-time updates
  const handleParticipantUpdate = useCallback((data) => {
    console.log("Participant update received:", data);
    setCarnivalUpdates((prev) => ({
      ...prev,
      [data.carnival_id]: {
        ...prev[data.carnival_id],
        current_participants: data.current_participants,
        max_participants: data.max_participants,
        lastUpdate: new Date().toISOString(),
      },
    }));

    // Update the carnivals list with new participant count
    setCarnivals((prevCarnivals) =>
      prevCarnivals.map((carnival) =>
        carnival.id === data.carnival_id
          ? { ...carnival, current_participants: data.current_participants }
          : carnival
      )
    );
  }, []);

  const handleStatusChange = useCallback((data) => {
    console.log("Status change received:", data);
    setCarnivalUpdates((prev) => ({
      ...prev,
      [data.carnival_id]: {
        ...prev[data.carnival_id],
        is_active: data.is_active,
        is_archived: data.is_archived,
        lastUpdate: new Date().toISOString(),
      },
    }));

    // Update the carnivals list with new status
    setCarnivals((prevCarnivals) =>
      prevCarnivals.map((carnival) =>
        carnival.id === data.carnival_id
          ? {
              ...carnival,
              is_active: data.is_active,
              is_archived: data.is_archived,
            }
          : carnival
      )
    );
  }, []);

  const handleWebSocketError = useCallback((error) => {
    console.error("WebSocket error:", error);
    showSnackbar(t("carnivals.realTimeUpdatesUnavailable"), "warning");
  }, []);

  const handleCreateCarnival = async () => {
    try {
      // Frontend validation
      const deadlineDate = new Date(formData.deadline);
      const startTimeDate = new Date(formData.start_time);

      // Debug timezone information
      console.log(
        "User timezone offset:",
        new Date().getTimezoneOffset() / -60
      );
      console.log("Original deadline:", deadlineDate.toString());
      console.log("Original start time:", startTimeDate.toString());

      if (startTimeDate <= deadlineDate) {
        showSnackbar(t("carnivals.startTimeAfterDeadline"), "error");
        return;
      }

      // Properly create UTC ISO strings
      const deadlineUTC = deadlineDate.toISOString();
      const startTimeUTC = startTimeDate.toISOString();

      console.log("UTC deadline:", deadlineUTC);
      console.log("UTC start time:", startTimeUTC);

      // Create a new object with UTC times
      const formDataUTC = {
        ...formData,
        deadline: deadlineUTC,
        start_time: startTimeUTC,
      };

      console.log("Data being sent to backend:", formDataUTC);

      // Send the data
      await axios.post(`${apiUrl}opportunities/api/carnivals/`, formDataUTC, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });

      fetchCarnivals(1);
      setOpenDialog(false);
      setFormData({
        title: "",
        description: "",
        deadline: "",
        start_time: "",
        max_participants: 100,
      });
      showSnackbar(t("carnivals.caravanCreatedSuccessfully"), "success");
    } catch (err) {
      showSnackbar(t("carnivals.errorCreatingCaravan"), "error");
      console.error("Error creating carnival:", err);
    }
  };

  const handleUpdateCarnival = async () => {
    try {
      // Frontend validation
      const deadlineDate = new Date(formData.deadline);
      const startTimeDate = new Date(formData.start_time);

      // Debug timezone information
      console.log(
        "User timezone offset:",
        new Date().getTimezoneOffset() / -60
      );
      console.log("Original deadline:", deadlineDate.toString());
      console.log("Original start time:", startTimeDate.toString());

      // Check if start time is after deadline
      if (startTimeDate <= deadlineDate) {
        showSnackbar(t("carnivals.startTimeAfterDeadline"), "error");
        return;
      }

      // Properly create UTC ISO strings
      const deadlineUTC = deadlineDate.toISOString();
      const startTimeUTC = startTimeDate.toISOString();

      console.log("UTC deadline:", deadlineUTC);
      console.log("UTC start time:", startTimeUTC);

      // Create a new object with UTC times
      const formDataUTC = {
        ...formData,
        deadline: deadlineUTC,
        start_time: startTimeUTC,
      };

      console.log("Data being sent to backend:", formDataUTC);

      // Send the data
      const response = await axios.put(
        `${apiUrl}opportunities/api/carnivals/${selectedCarnivalToEdit.id}/`,
        formDataUTC,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );

      fetchCarnivals(pagination.page);
      setEditDialogOpen(false);
      showSnackbar(t("carnivals.caravanUpdatedSuccessfully"), "success");
    } catch (err) {
      // Better error handling
      if (err.response && err.response.data) {
        const errorMessage = err.response.data.non_field_errors
          ? err.response.data.non_field_errors[0]
          : "Error updating carnival";
        showSnackbar(t("carnivals.errorUpdatingCaravan"), "error");
        console.error("Server error details:", err.response.data);
      } else {
        showSnackbar(t("carnivals.errorUpdatingCaravan"), "error");
      }
      console.error("Error updating carnival:", err);
    }
  };

  const fetchCarnivals = async (page = 1) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${apiUrl}opportunities/api/carnivals/`,
        {
          params: {
            is_archived: viewMode === "archived",
            page: page,
          },
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );
      setCarnivals(response.data.results);
      setPagination({
        page: page,
        totalPages: Math.ceil(response.data.count / 10),
        totalItems: response.data.count,
      });
      console.log("start", response.data.results.start_time);
      setError(null);
    } catch (err) {
      setError(t("carnivals.failedToLoadCaravans"));
      console.error("Error fetching carnivals:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCarnivals(1);
  }, [viewMode]);

  // WebSocket connections for real-time updates
  const carnivalWebSockets = {};

  useEffect(() => {
    // Create WebSocket connections for each carnival
    carnivals.forEach((carnival) => {
      if (!carnivalWebSockets[carnival.id] && carnival.id) {
        // Create WebSocket connection for this carnival
        const wsOptions = {
          onParticipantUpdate: handleParticipantUpdate,
          onStatusChange: handleStatusChange,
          onError: handleWebSocketError,
          debug: process.env.NODE_ENV === "development",
        };

        // Note: We'll create individual connections as needed
        // For now, we'll use the hook in the CarnivalCard components
      }
    });

    // Cleanup function
    return () => {
      Object.values(carnivalWebSockets).forEach((ws) => {
        if (ws && ws.disconnect) {
          ws.disconnect();
        }
      });
    };
  }, [
    carnivals,
    handleParticipantUpdate,
    handleStatusChange,
    handleWebSocketError,
  ]);

  // Pagination handler
  const handlePageChange = (event, newPage) => {
    fetchCarnivals(newPage);
  };

  const handleToggleArchive = async (carnival) => {
    try {
      // If trying to unarchive, check if deadline has passed
      if (carnival.is_archived) {
        const deadlineDate = new Date(carnival.deadline);
        const currentDate = new Date();

        // If deadline has passed, show error message and don't proceed with unarchiving
        if (deadlineDate < currentDate) {
          setCarnivalToUnarchive(carnival);
          setConfirmUnarchiveDialogOpen(true);
          return;
        }
      }

      // Proceed with toggle archive API call
      await axios.post(
        `${apiUrl}opportunities/api/carnivals/${carnival.id}/toggle_archive/`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );

      fetchCarnivals(pagination.page);
      showSnackbar(
        carnival.is_archived ? t("carnivals.caravanUnarchived") : t("carnivals.caravanArchived"),
        "success"
      );
    } catch (err) {
      showSnackbar(t("carnivals.errorUpdatingStatus"), "error");
      console.error("Error toggling archive status:", err);
    }
  };
  const handleConfirmUnarchive = async () => {
    try {
      await axios.post(
        `${apiUrl}opportunities/api/carnivals/${carnivalToUnarchive.id}/toggle_archive/`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );

      fetchCarnivals(pagination.page);
      showSnackbar(t("carnivals.caravanUnarchived"), "success");
      setConfirmUnarchiveDialogOpen(false);
    } catch (err) {
      showSnackbar(t("carnivals.errorUpdatingStatus"), "error");
      console.error("Error toggling archive status:", err);
    }
  };

  // Helper functions
  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleEditCarnival = (carnival) => {
    setSelectedCarnivalToEdit(carnival);
    setFormData({
      title: carnival.title,
      description: carnival.description,
      deadline: carnival.deadline.slice(0, 16),
      start_time: carnival.start_time.slice(0, 16),
      max_participants: carnival.max_participants,
    });
    setEditDialogOpen(true);
  };

  const handleShareCarnival = (carnival) => {
    // Encode the carnival ID using Base64
    const encodedId = btoa(carnival.id.toString());
    // Use the environment variable instead of window.location.origin
    const shareUrl = `${process.env.REACT_APP_API_FRONT_URL}carnival/${encodedId}`;
    setShareLink(shareUrl);
    setSharedCarnival(carnival);
    setShareDialogOpen(true);
  };

  if (selectedCarnival) {
    return (
      <ThemeProvider theme={theme}>
        <Box sx={{ p: 3, bgcolor: "background.default", minHeight: "100vh" }}>
          <Button
            variant="outlined"
            onClick={() => {
              setSelectedCarnival(null);
              fetchCarnivals(pagination.page);
            }}
            sx={{ mb: 2 }}
          >
            {t("carnivals.backToCaravans")}
          </Button>
          <CarnivalVideoCall carnivalId={selectedCarnival.id} />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ p: 3, bgcolor: "background.default", minHeight: "100vh" }}>
        {/* Unarchive Confirmation Dialog */}
        <Dialog
          open={confirmUnarchiveDialogOpen}
          onClose={() => setConfirmUnarchiveDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{t("carnivals.confirmUnarchive")}</DialogTitle>
          <DialogContent>
            <Typography variant="body1">
              {carnivalToUnarchive && (
                <>
                  {t("carnivals.confirmUnarchiveMessage", { deadline: format(new Date(carnivalToUnarchive.deadline), "PPp") })}
                </>
              )}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setConfirmUnarchiveDialogOpen(false)}
              variant="outlined"
            >
              {t("carnivals.cancel")}
            </Button>
            <Button
              onClick={handleConfirmUnarchive}
              variant="contained"
              color="primary"
            >
              {t("carnivals.unarchiveAnyway")}
            </Button>
          </DialogActions>
        </Dialog>
        {/* View Mode Toggle */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant={viewMode === "active" ? "contained" : "outlined"}
              onClick={() => setViewMode("active")}
              sx={{ px: 3, py: 1 }}
            >
              {t("carnivals.activeCaravans")}
            </Button>
            <Button
              variant={viewMode === "archived" ? "contained" : "outlined"}
              onClick={() => setViewMode("archived")}
              sx={{ px: 3, py: 1 }}
            >
              {t("carnivals.archivedCaravans")}
            </Button>
          </Box>
          {viewMode === "active" && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => setOpenDialog(true)}
              sx={{
                ml: "auto",
                px: 3,
                py: 1.5,
                fontSize: "1rem",
                fontWeight: 600,
                borderRadius: 2,
                background: "linear-gradient(45deg, #0017E7 30%, #1e40af 90%)",
                boxShadow: "0px 4px 12px rgba(0, 23, 231, 0.3)",
                "&:hover": {
                  background:
                    "linear-gradient(45deg, #0010a3 30%, #1e3a8a 90%)",
                  boxShadow: "0px 6px 16px rgba(0, 23, 231, 0.4)",
                  transform: "translateY(-1px)",
                },
                transition: "all 0.2s ease-in-out",
              }}
            >
              {t("carnivals.createNew")}
            </Button>
          )}
        </Box>
        {/* Add Search Bar Here */}
        <SearchBarCarnivals
          onSearchResults={handleSearchResults}
          setLoading={setLoading}
          viewMode={viewMode}
        />

        {/* Create Carnival Dialog */}
        <CarnivalFormDialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleCreateCarnival}
          mode="create"
        />

        {/* Edit Carnival Dialog */}
        <CarnivalFormDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleUpdateCarnival}
          mode="edit"
        />

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Error State */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        {/* Carnival List */}
        <Box sx={{ display: "grid", gap: 3 }}>
          {(searchResults || carnivals).map((carnival) => (
            <CarnivalCard
              key={carnival.id}
              carnival={carnival}
              onEdit={handleEditCarnival}
              onToggleArchive={handleToggleArchive}
              onSelect={setSelectedCarnival}
              onShare={handleShareCarnival}
              showSnackbar={showSnackbar}
            />
          ))}
        </Box>

        {/* Share Carnival Dialog */}
        <ShareCarnivalDialog
          open={shareDialogOpen}
          onClose={() => setShareDialogOpen(false)}
          shareLink={shareLink}
          sharedCarnival={sharedCarnival}
          showSnackbar={showSnackbar}
        />

        {/* Pagination */}
        {!loading && !searchResults && carnivals.length > 0 && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <Pagination
              count={pagination.totalPages}
              page={pagination.page}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
              sx={{
                "& .MuiPaginationItem-root": {
                  borderRadius: 2,
                },
              }}
            />
          </Box>
        )}

        {/* Empty State */}
        {!loading &&
          (searchResults?.length === 0 || carnivals.length === 0) && (
            <Box
              sx={{
                textAlign: "center",
                p: 6,
                border: "1px dashed",
                borderColor: "primary.light",
                borderRadius: 3,
                mt: 4,
                backgroundColor: "rgba(25, 118, 210, 0.02)",
              }}
            >
              <Typography variant="h6" color="text.secondary">
                {searchResults?.length === 0
                  ? t("carnivals.noCaravansFound")
                  : viewMode === "archived" ? t("carnivals.noArchivedCaravans") : t("carnivals.noActiveCaravans")}
              </Typography>
            </Box>
          )}

        {/* Snackbar Notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            variant="filled"
            sx={{
              borderRadius: 2,
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
};

export default RecruiterDashboard;
